import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faChevronDown, faChevronUp, faExchangeAlt, faSatelliteDish, faSliders } from "@fortawesome/free-solid-svg-icons";
import Layout from "../../components/layout/layout";
import axiosInstance from "../../AxiosInstance";
import usePolling from "../../hooks/usePolling";
import { useSidebar } from "../../contexts/SidebarContext";
import { metricsFor } from "../../utils/netquality";
import CoverageMap from "./components/coverage_map";
import ControlsPanel from "./components/controls_panel";
import Legend from "./components/legend";
import KpiStrip from "./components/kpi_strip";
import TimelineChart from "./components/timeline_chart";
import ChangesPanel from "./components/changes_panel";
import ProbeNow from "./components/probe_now";
import styles from "./network_map.module.css";

// El mapa se refresca solo: los equipos miden cada pocos minutos.
const HEATMAP_POLL_MS = 60000;
const PREFS_KEY = "pdui_netmap_prefs";

function loadPrefs() {
  try {
    return JSON.parse(window.localStorage.getItem(PREFS_KEY) || "{}");
  } catch (e) {
    return {};
  }
}

function savePrefs(prefs) {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch (e) {
    // sin storage no se recuerda
  }
}

/**
 * Cobertura de red: mapa de celdas coloreadas por la calidad medida desde
 * los equipos (señal, latencia, pérdida, velocidad), con evolución en el
 * tiempo, zonas que empeoraron y medición a pedido.
 */
export default function NetworkMap() {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  const [searchParams] = useSearchParams();
  const prefs = useMemo(loadPrefs, []);

  const [iface, setIface] = useState(prefs.iface || "wifi");
  const [metric, setMetric] = useState(prefs.metric || "score");
  const [days, setDays] = useState(prefs.days || 7);
  const [cellM, setCellM] = useState(prefs.cellM || 50);
  const [pdui, setPdui] = useState(searchParams.get("pdui") || "all");
  const [connectedOnly, setConnectedOnly] = useState(false);
  const [showChanges, setShowChanges] = useState(true);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(!isMobile);
  // Aviso cerrado con la cruz: solo durante esta visita. Al recargar, o al
  // cambiar de interfaz, vuelve a salir si la situación sigue igual.
  const [dismissedBanner, setDismissedBanner] = useState(null);
  const [tab, setTab] = useState(searchParams.get("pdui") ? "probe" : "timeline");

  const [devices, setDevices] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [changes, setChanges] = useState(null);
  const [changesError, setChangesError] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [focus, setFocus] = useState(null);
  const autoSwitched = useRef(false);

  useEffect(() => {
    savePrefs({ iface, metric, days, cellM });
  }, [iface, metric, days, cellM]);

  // Si la métrica elegida no aplica a la red (RSRP solo en LTE), volver a la general.
  useEffect(() => {
    if (!metricsFor(iface).includes(metric)) setMetric("score");
  }, [iface, metric]);

  useEffect(() => {
    axiosInstance
      .get("pdui/")
      .then((response) => setDevices((response.data || []).map((d) => ({ id: d.id, name: d.name }))))
      .catch(() => {});
  }, []);

  const heatmapQuery = `network_heatmap/?interface=${iface}&days=${days}&cell=${cellM}&pdui=${pdui}&connected_only=${connectedOnly ? 1 : 0}`;

  usePolling(
    (isActive) =>
      axiosInstance
        .get(heatmapQuery)
        .then((response) => {
          if (!isActive()) return;
          const data = response.data;
          setHeatmap(data);
          setError(false);
          // Primera carga sin datos en la red elegida: saltar a la que sí tiene.
          if (!autoSwitched.current) {
            autoSwitched.current = true;
            const avail = data.available || {};
            if (!avail[iface]) {
              const best = Object.keys(avail).sort((a, b) => (avail[b] || 0) - (avail[a] || 0))[0];
              if (best && avail[best] > 0) setIface(best);
            }
          }
        })
        .catch(() => {
          if (isActive()) setError(true);
        })
        .finally(() => {
          if (isActive()) setLoading(false);
        }),
    HEATMAP_POLL_MS,
    true,
    heatmapQuery,
  );

  const reloadAnalysis = useCallback(() => {
    axiosInstance
      .get(`network_changes/?interface=${iface}&cell=${cellM}&pdui=${pdui}`)
      .then((response) => {
        setChanges(response.data);
        setChangesError(false);
      })
      .catch(() => setChangesError(true));
    axiosInstance
      .get(`network_timeline/?interface=${iface}&days=${days}&pdui=${pdui}&connected_only=${connectedOnly ? 1 : 0}`)
      .then((response) => setTimeline(response.data))
      .catch(() => setTimeline(null));
  }, [iface, cellM, pdui, days, connectedOnly]);

  useEffect(() => {
    reloadAnalysis();
  }, [reloadAnalysis]);

  const cells = heatmap?.cells || [];
  // `available`: mediciones con calidad (el mismo número que el indicador de
  // flota). `sampled`: todas las lecturas, también las que no puntúan porque
  // la red no estaba conectada o no hubo prueba activa.
  const sampleCount = heatmap?.available ? heatmap.available[iface] || 0 : 0;
  const sampledCount = heatmap?.sampled ? heatmap.sampled[iface] || 0 : 0;
  const noData = !loading && !error && cells.length === 0;
  const bannerKind = error
    ? "error"
    : sampleCount > 0
      ? "noGps"
      : sampledCount > 0
        ? "noQuality"
        : "noData";
  const bannerKey = `${bannerKind}:${iface}`;
  const showBanner = (noData || error) && dismissedBanner !== bannerKey;
  const ifaceName = t(`netmap.iface.${iface}`);
  const worsenedCount = changes?.worsened?.length || 0;

  const goTo = (item) => {
    setFocus({ ...item, _ts: Date.now() });
    if (isMobile) setPanelOpen(false);
  };

  return (
    <Layout selectedIndex={-1}>
      <div className={styles.page}>
        <div className={styles.mapArea}>
          <CoverageMap
            center={heatmap?.center}
            cells={cells}
            metric={metric}
            iface={iface}
            devices={heatmap?.devices || []}
            changes={changes}
            showChanges={showChanges}
            focus={focus}
            onCellSelect={() => {}}
          />

          {loading && <div className={styles.loadingDot}>…</div>}

          <div className={styles.topStack}>
            <KpiStrip summary={heatmap?.summary} iface={iface} setIface={setIface} days={days} />

            {showBanner && (
              <div className={`${styles.banner} ${error ? styles.bannerError : ""}`} role="status">
                <button
                  type="button"
                  className={styles.bannerClose}
                  onClick={() => setDismissedBanner(bannerKey)}
                  aria-label={t("netmap.dismiss")}
                  title={t("netmap.dismiss")}
                >
                  ×
                </button>
                {bannerKind === "error" && t("netmap.loadError")}
                {bannerKind === "noGps" && t("netmap.noGps", { iface: ifaceName, n: sampleCount })}
                {bannerKind === "noQuality" && t("netmap.noQuality", { iface: ifaceName })}
                {bannerKind === "noData" && t("netmap.noData", { iface: ifaceName })}
                {bannerKind !== "error" && (
                  <div className={styles.bannerHint}>{t(`netmap.${bannerKind}Hint`, { iface: ifaceName })}</div>
                )}
              </div>
            )}
          </div>

          <button type="button" className={styles.fab} onClick={() => setControlsOpen(true)}>
            <FontAwesomeIcon icon={faSliders} /> {t("netmap.filters")}
          </button>

          <ControlsPanel
            open={controlsOpen}
            onClose={() => setControlsOpen(false)}
            iface={iface}
            setIface={setIface}
            metric={metric}
            setMetric={setMetric}
            days={days}
            setDays={setDays}
            cellM={cellM}
            setCellM={setCellM}
            pdui={pdui}
            setPdui={setPdui}
            devices={devices}
            connectedOnly={connectedOnly}
            setConnectedOnly={setConnectedOnly}
            showChanges={showChanges}
            setShowChanges={setShowChanges}
            available={heatmap?.available}
            cellCount={cells.length}
            sampleCount={sampleCount}
          />

          <Legend metric={metric} iface={iface} />
        </div>

        <div className={`${styles.panel} ${panelOpen ? "" : styles.panelClosed}`}>
          <div className={styles.panelBar}>
            <button type="button" className={`${styles.tab} ${tab === "timeline" ? styles.tabActive : ""}`} onClick={() => { setTab("timeline"); setPanelOpen(true); }}>
              <FontAwesomeIcon icon={faChartLine} /> {t("netmap.tabs.timeline")}
            </button>
            <button type="button" className={`${styles.tab} ${tab === "changes" ? styles.tabActive : ""}`} onClick={() => { setTab("changes"); setPanelOpen(true); }}>
              <FontAwesomeIcon icon={faExchangeAlt} /> {t("netmap.tabs.changes")}
              {worsenedCount > 0 && <span className={styles.tabBadge}>{worsenedCount}</span>}
            </button>
            <button type="button" className={`${styles.tab} ${tab === "probe" ? styles.tabActive : ""}`} onClick={() => { setTab("probe"); setPanelOpen(true); }}>
              <FontAwesomeIcon icon={faSatelliteDish} /> {t("netmap.tabs.probe")}
            </button>
            <button type="button" className={styles.panelToggle} onClick={() => setPanelOpen((p) => !p)}>
              {panelOpen ? t("netmap.panelClose") : t("netmap.panelOpen")}
              <FontAwesomeIcon icon={panelOpen ? faChevronDown : faChevronUp} />
            </button>
          </div>
          <div className={styles.panelBody}>
            {tab === "timeline" && (
              <TimelineChart points={timeline?.points} bucket={timeline?.bucket} metric={metric} iface={iface} />
            )}
            {tab === "changes" && <ChangesPanel changes={changes} error={changesError} onGo={goTo} />}
            {tab === "probe" && (
              <ProbeNow
                devices={devices}
                initialPdui={pdui !== "all" ? pdui : ""}
                onResults={reloadAnalysis}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
